import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { cronKeys, feedKeys, presetKeys } from '~/lib/query-keys';

/**
 * Pull a human-readable message off an ApiError thrown by the api client.
 * Errors carry the response body in `.message`; the server sends `{error: …}`
 * JSON for cron CRUD failures (incl. the 504 we return when the master agent
 * is wedged behind a paused invocation).
 */
function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) {
    try {
      const parsed = JSON.parse(err.message);
      if (parsed?.error) return parsed.error;
    } catch { /* not JSON */ }
    return err.message.slice(0, 200);
  }
  return fallback;
}

export function useCreateCron() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: any) => api.post<{ id: string; needsAuth?: boolean }>('/api/crons', body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: cronKeys.all });
      // FRONT-666: a job that lands without a usable credential won't run
      // until authorized — say so now instead of letting the user assume
      // it's live and only discover the small "Needs auth" badge later.
      if (data?.needsAuth) {
        toast.warning('Scheduled — but this job needs authorization', {
          description: "It won't run until you authorize it from the Schedule page or your notifications.",
        });
      }
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to create schedule')),
  });
}

export function useUpdateCron() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; [key: string]: any }) => api.put(`/api/crons/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: cronKeys.all }),
    onError: (err) => toast.error(errorMessage(err, 'Failed to update schedule')),
  });
}

export function useDeleteCron() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/crons/${id}`),
    // Deleting a preset-born cron also records a dismissal server-side, so
    // the catalog's contents change on a plain cron delete too.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cronKeys.all });
      qc.invalidateQueries({ queryKey: presetKeys.all });
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to delete schedule')),
  });
}

export interface BulkCronDeleteResult {
  id: string;
  ok: boolean;
  error?: string;
}

/**
 * Pairs each job id with its settled outcome. Pure and exported so it's
 * directly unit-testable without a mutation/DOM harness — same convention as
 * `use-skill-mutations.ts`'s `toBulkDeleteResults`, which this bulk "Delete
 * Job" picker mirrors.
 */
export function toBulkCronDeleteResults(
  ids: string[],
  settled: PromiseSettledResult<unknown>[],
): BulkCronDeleteResult[] {
  return settled.map((r, i) => ({
    id: ids[i],
    ok: r.status === 'fulfilled',
    error: r.status === 'rejected' ? (r.reason as Error).message : undefined,
  }));
}

/**
 * Deletes multiple cron jobs in one action — the Schedule page's bulk
 * "Delete Job" picker (mirrors the Skills page's "Delete Skill" picker,
 * FRONT-1753). No bulk backend route exists or is needed: DELETE
 * /api/crons/:id already exists and job counts are small, so this fires one
 * request per id and settles them all (Promise.allSettled, not Promise.all —
 * one bad id must not stop the rest from deleting) before reporting which
 * succeeded.
 */
export function useDeleteCrons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkCronDeleteResult[]> => {
      const settled = await Promise.allSettled(ids.map((id) => api.delete(`/api/crons/${id}`)));
      return toBulkCronDeleteResults(ids, settled);
    },
    onSuccess: (results) => applyBulkCronDeleteResults(qc, results),
    // code-reviewer review on #1211: every OTHER mutation in this file has
    // this — this one didn't, so `mutateAsync` rejecting inside the dialog's
    // own onSuccess (e.g. a cache-invalidation error) surfaced nothing at
    // all: no toast, dialog stuck open with the spinner cleared.
    onError: (err) => toast.error(errorMessage(err, 'Could not delete the selected jobs')),
  });
}

/**
 * What the cache does after a bulk delete — drop each SUCCESSFULLY deleted
 * job's own cache entry rather than invalidating it (a refetch of a
 * just-deleted id is a guaranteed 404), leave a failed id's cache alone (it's
 * still a real row), then refresh the list once for the whole batch
 * regardless of size. Exported as its own pure(ish) function so it is
 * directly testable — same shape as `use-skill-mutations.ts`'s
 * `applyBulkDeleteResults`.
 *
 * Invalidates `cronKeys.all`, not just `cronKeys.list()` (code-reviewer
 * review on #1211): `cronKeys.authStatus()` is a map keyed by cron id, and
 * `cron-list.tsx`'s "needs authorization" count reads every entry in it — a
 * narrower invalidation left deleted jobs' stale auth-status entries
 * counted in that badge until the next unrelated refetch. Invalidation only
 * refetches queries that are actually mounted, so this doesn't risk a 404
 * on a dead detail/runs query the way re-fetching a just-deleted id's own
 * detail key would.
 */
export function applyBulkCronDeleteResults(qc: QueryClient, results: BulkCronDeleteResult[]): void {
  for (const r of results) {
    if (r.ok) qc.removeQueries({ queryKey: cronKeys.detail(r.id) });
  }
  void qc.invalidateQueries({ queryKey: cronKeys.all });
  // Same reason as useDeleteCron: a deleted preset-born cron is a dismissal,
  // so the suggestion catalog is stale after a bulk delete too.
  void qc.invalidateQueries({ queryKey: presetKeys.all });
}

/**
 * Permanently delete ONE cron run (a result in the inbox feed). Owner-only
 * server-side (FRONT-191). Refreshes both the feed and any open run lists.
 */
export function useDeleteCronRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: number) => api.delete(`/api/crons/runs/${runId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.all });
      qc.invalidateQueries({ queryKey: cronKeys.all });
    },
    onError: (err) => toast.error(errorMessage(err, 'Failed to delete result')),
  });
}

/**
 * Bulk recycle-bin action on cron-run results (FRONT-594): trash / restore /
 * purge a set of run ids in one request. Refreshes the feed (live + bin) and
 * any open run lists.
 */
export function useBulkCronRuns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ids }: { action: 'trash' | 'restore' | 'purge'; ids: number[] }) =>
      api.post<{ affected: number }>('/api/crons/runs/bulk', { action, ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.all });
      qc.invalidateQueries({ queryKey: cronKeys.all });
    },
    onError: (err) => toast.error(errorMessage(err, 'Action failed')),
  });
}

export function useToggleCron() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`/api/crons/${id}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: cronKeys.all }),
    onError: (err) => toast.error(errorMessage(err, 'Failed to toggle schedule')),
  });
}

export type CronAuthEntry = {
  hasAuth: boolean;
  expiresAt?: string;
  // Most recent run died on an auth-related error (e.g. Zitadel rejected the
  // stored refresh token). The encrypted row can still look healthy locally;
  // this is the only signal that upstream has revoked the credential.
  lastRunAuthFailed?: boolean;
  // Short snippet of the failing run's last result, for tooltip use.
  lastResult?: string;
  // System cron (e.g. reflect): authenticates per user at fire time, so there
  // is no single credential to re-auth. The UI shows these as self-authing
  // rather than offering a (meaningless, 403-prone) re-auth (FRONT-226).
  system?: boolean;
};

export type CronAuthStatus = Record<string, CronAuthEntry>;

export function useCronAuthStatus() {
  return useQuery({
    queryKey: cronKeys.authStatus(),
    queryFn: () => api.get<CronAuthStatus>('/api/crons/auth-status'),
  });
}

export function useReauthCron() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/cron/${id}/reauth`),
    onSuccess: () => qc.invalidateQueries({ queryKey: cronKeys.authStatus() }),
  });
}

export interface ClaudeTaskDraft {
  name: string;
  schedule: string;
  taskText: string;
}

export interface ClaudeTaskExtractResult {
  draft: ClaudeTaskDraft;
  /** True when extraction could not produce a usable draft — either the
   *  extraction pass itself threw (LLM/model failure), OR it resolved
   *  normally but the parsed draft came back entirely empty (the model
   *  replied with prose instead of JSON, the more common failure since
   *  that path never throws). Both collapse to the same empty `draft`;
   *  `degraded` is what lets the caller tell that apart from a genuine
   *  "found nothing" result and warn only on this one (silent-failure /
   *  Aikido review on #1211: an earlier version of this comment said
   *  "throws" only, which `src/cron/routes.ts`'s actual check —
   *  `!draft.name && !draft.schedule && !draft.taskText` — never claimed;
   *  they used to be wire-identical either way). */
  degraded: boolean;
}

/** FRONT-1695 / ADR-0049: one non-streamed LLM pass over a freeform
 *  description (e.g. of a Claude Scheduled Task), returning a draft the
 *  "New Scheduled Job" form can be pre-filled with. Writes nothing. */
export function useExtractClaudeTask() {
  return useMutation({
    mutationFn: (description: string) =>
      api.post<ClaudeTaskExtractResult>('/api/crons/extract', { description }),
    onError: (err) => toast.error(errorMessage(err, 'Could not read that description')),
  });
}

export function useRunCronNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ id: string; queued: boolean }>(`/api/crons/${id}/run`),
    onSuccess: () => {
      // Result lands asynchronously via the event bus / next refetch; nudge
      // the list now so the user sees runCount/lastRunAt update soon.
      qc.invalidateQueries({ queryKey: cronKeys.all });
    },
  });
}
